import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { api } from "~/lib/api";
import { usePeriod } from "~/lib/period";
import type {
  InsightsResponse,
  InsightItem,
  InsightSeverity,
} from "@aura/shared";
import { buildInsightsDek, buildInsightsHeadline } from "./story";
import { ed } from "~/components/editorial";
import { periodName } from "~/lib/format";

const SEVERITY_ORDER: InsightSeverity[] = ["critical", "warning", "info"];

const SEVERITY_LABEL: Record<InsightSeverity, string> = {
  critical: "Critical",
  warning: "Needs attention",
  info: "Worth a look",
};

export function InsightsPage() {
  const { range, window: periodWin } = usePeriod();

  const { data, isLoading, error } = useQuery({
    queryKey: ["insights", range],
    queryFn: () => api.get<{ data: InsightsResponse }>(`/insights?range=${range}`),
    staleTime: 60 * 1000,
  });

  const summary = data?.data.summary;
  const items = data?.data.items ?? [];
  const generatedAt = data?.data.generatedAt ? new Date(data.data.generatedAt) : null;
  const asOf =
    generatedAt && !Number.isNaN(generatedAt.getTime())
      ? `${generatedAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} ${generatedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
      : null;

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <header className={ed.masthead}>
          <span className={ed.mastTitle}>Insights</span>
          <span className={ed.mastMeta}>
            {periodWin.label} vs. {periodWin.prevLabel}
            {asOf && ` · computed ${asOf}`}
          </span>
        </header>

        <div className={ed.kicker}>What needs attention · {periodName(periodWin.label)}</div>
        {isLoading ? (
          <div className={ed.loadingSm}>Computing findings…</div>
        ) : error || !summary ? (
          <>
            <h1 className={ed.headline}>Findings didn't load</h1>
            <p className={ed.dek}>
              {error ? String((error as Error).message) : "The insights endpoint returned nothing."}
            </p>
          </>
        ) : (
          <>
            <h1 className={ed.headline}>{buildInsightsHeadline(summary)}</h1>
            <p className={ed.dek}>{buildInsightsDek(items)}</p>
            <div className={ed.byline}>
              <b>Insights</b> · ranked findings across delivery, quality, AI, projects and
              people, computed from GitHub, JIRA and tool data. No model writes these.
            </div>

            <dl className={styles.tally}>
              {SEVERITY_ORDER.map((s) => (
                <div key={s} className={styles.tallyCell} data-flag={s === "critical" && summary.critical > 0 ? "caution" : undefined}>
                  <dt className={styles.tallyLabel}>{SEVERITY_LABEL[s]}</dt>
                  <dd className={styles.tallyValue}>{summary[s]}</dd>
                </div>
              ))}
            </dl>

            {items.length === 0 ? (
              <>
                <div className={ed.sect}>All clear</div>
                <p className={ed.prose}>
                  No stale PRs, review pileups, failure spikes, idle seats or sprints at risk in
                  this window.
                </p>
              </>
            ) : (
              SEVERITY_ORDER.map((sev, idx) => {
                const group = items.filter((i) => i.severity === sev);
                if (group.length === 0) return null;
                return (
                  <section key={sev}>
                    <div className={ed.sect}>
                      {["I", "II", "III"][idx]}. {SEVERITY_LABEL[sev]}
                    </div>
                    <ol className={styles.list}>
                      {group.map((item) => (
                        <InsightRow key={item.id} item={item} />
                      ))}
                    </ol>
                  </section>
                );
              })
            )}

            <p className={ed.method}>
              Each finding is a fixed rule (stale PRs, PRs waiting on review, change failure
              spikes, idle AI tool seats, sprints at risk and so on), ranked by severity and then
              by size. Some rules read the selected period, others the current state.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function InsightRow({ item }: { item: InsightItem }) {
  return (
    <li className={styles.row}>
      <div className={styles.metric}>{item.metric}</div>
      <div className={styles.body}>
        <div className={styles.cat}>{item.category}</div>
        <h3 className={styles.title}>
          <Link to={item.link}>{item.title}</Link>
        </h3>
        <p className={styles.detail}>{item.detail}</p>
      </div>
      <Link to={item.link} className={styles.action}>
        {item.linkLabel} →
      </Link>
    </li>
  );
}

const styles = {
  tally: css({
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    borderTopWidth: "2px",
    borderTopStyle: "solid",
    borderColor: "ed.ink",
    m: 0,
    maxWidth: "560px",
  }),
  tallyCell: css({
    px: "12px",
    pt: "10px",
    pb: "12px",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
    "&[data-flag=caution] dd": { bg: "ed.cautionTint", px: "4px" },
  }),
  tallyLabel: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
    mb: "6px",
  }),
  tallyValue: css({
    m: 0,
    display: "inline-block",
    fontFamily: "mono",
    fontVariantNumeric: "tabular-nums",
    fontSize: "26px",
    fontWeight: 600,
    lineHeight: 1.1,
  }),
  list: css({ listStyle: "none", m: 0, p: 0, mt: "4px" }),
  row: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", md: "120px minmax(0, 1fr) auto" },
    gap: { base: "4px", md: "20px" },
    alignItems: "baseline",
    py: "14px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
  }),
  metric: css({
    fontFamily: "mono",
    fontVariantNumeric: "tabular-nums",
    fontSize: "22px",
    fontWeight: 600,
    letterSpacing: "-0.02em",
    color: "ed.ink",
  }),
  body: css({ minWidth: 0 }),
  cat: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "ed.ink3",
    mb: "2px",
  }),
  title: css({
    fontFamily: "serif",
    fontSize: "19px",
    fontWeight: 700,
    lineHeight: 1.25,
    m: 0,
    "& a": {
      textDecoration: "none",
      _hover: { textDecoration: "underline", textUnderlineOffset: "3px" },
    },
  }),
  detail: css({
    fontFamily: "serif",
    fontSize: "15.5px",
    lineHeight: 1.5,
    color: "ed.ink2",
    m: 0,
    mt: "4px",
    maxWidth: "680px",
  }),
  action: css({
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 600,
    color: "ed.ink",
    whiteSpace: "nowrap",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    textDecorationColor: "ed.rule",
    _hover: { textDecorationColor: "ed.ink" },
  }),
};
